import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';

function PasswordInput({ value, onChange, placeholder, label }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="form-label">{label}</label>
      <div style={{ position:'relative' }}>
        <input
          className="form-input"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{ paddingRight: 40 }}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          style={{
            position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
            background:'none', border:'none', cursor:'pointer',
            color:'var(--text-tertiary)', fontSize:12, padding:0
          }}
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  useEffect(() => {
    api.get('/integrations/outlook/status')
      .then(r => setOutlookStatus(r.data))
      .catch(() => {});
  }, []);

  const handlePasswordChange = async () => {
    setPwError('');
    setPwSuccess('');

    if (!pwForm.current_password) return setPwError('Current password is required.');
    if (pwForm.new_password.length < 8) return setPwError('New password must be at least 8 characters.');
    if (pwForm.new_password !== pwForm.confirm_password) return setPwError('New passwords do not match.');

    setPwSaving(true);
    try {
      await api.post('/auth/login', { email: user.email, password: pwForm.current_password });
      await api.patch(`/users/${user.id}/password`, { password: pwForm.new_password });
      setPwSuccess('Password updated successfully.');
      setPwForm({ current_password:'', new_password:'', confirm_password:'' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to update password. Check your current password.');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <>
      <div className="two-col">
        <div className="card">
          <div className="section-title">Your account</div>
          <div style={{ display:'flex', alignItems:'center', gap:12, margin:'12px 0 16px' }}>
            <div className="avatar" style={{ width:44, height:44, fontSize:14 }}>
              {user?.avatar_initials || '?'}
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:500 }}>{user?.full_name}</div>
              <div style={{ fontSize:12, color:'var(--text-secondary)' }}>{user?.email}</div>
              <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:2 }}>{user?.role}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="section-title">Integration status</div>
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon">✉</div>
              <div>
                <div className="activity-text">Outlook add-in</div>
                <div className="activity-time">
                  {outlookStatus?.connected
                    ? <><span className="status-dot connected"></span>Connected · {outlookStatus.connection.email}</>
                    : <><span className="status-dot error"></span>Not connected</>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="card" style={{ marginTop:14 }}>
        <div className="section-title">Change password</div>
        <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:12 }}>
          Use the form below to update your password.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10, maxWidth:320 }}>
          <PasswordInput
            label="Current password"
            value={pwForm.current_password}
            onChange={e => setPwForm({...pwForm, current_password:e.target.value})}
            placeholder="••••••••"
          />
          <PasswordInput
            label="New password (min 8 characters)"
            value={pwForm.new_password}
            onChange={e => setPwForm({...pwForm, new_password:e.target.value})}
            placeholder="••••••••"
          />
          <PasswordInput
            label="Confirm new password"
            value={pwForm.confirm_password}
            onChange={e => setPwForm({...pwForm, confirm_password:e.target.value})}
            placeholder="••••••••"
          />
          {pwError && <div className="form-error">{pwError}</div>}
          {pwSuccess && <div style={{ fontSize:12, color:'var(--success)', marginTop:4 }}>{pwSuccess}</div>}
          <button className="btn btn-primary" style={{ alignSelf:'flex-start' }}
            onClick={handlePasswordChange} disabled={pwSaving}>
            {pwSaving ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop:14 }}>
        <div className="section-title">About</div>
        <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.7 }}>
          <div>CRM v1.0.0</div>
          <div>Backend running on port 4000</div>
          <div>Database: PostgreSQL 18</div>
        </div>
      </div>
    </>
  );
}